// Projects Vuex module 
import projectService from '@/services/projectService';
import mediaService from '@/services/mediaService';
import { getMediaUrl } from '@/utils/mediaUtils';

export default {
  namespaced: true,
  
  state: {
    projects: [],
    featuredProjects: [],
    currentProject: null,
    relatedProjects: [],
    projectMedia: [],
    searchResults: [],
    filters: {
      projectType: null,
      clientType: null,
      year: null,
      location: null,
      serviceArea: null
    },
    loading: false,
    error: null
  },
  
  getters: {
    allProjects: state => state.projects,
    featuredProjects: state => state.featuredProjects,
    currentProject: state => state.currentProject,
    relatedProjects: state => state.relatedProjects,
    projectMedia: state => state.projectMedia,
    searchResults: state => state.searchResults,
    filters: state => state.filters,
    isLoading: state => state.loading,
    hasError: state => state.error !== null,
    getErrorMessage: state => state.error
  },
  
  mutations: {
    SET_PROJECTS(state, projects) {
      state.projects = projects.map(project => ({
        ...project,
        cover_image: getMediaUrl(project.cover_image),
        images: project.images?.map(img => getMediaUrl(img)) || []
      }));
    },
    SET_FEATURED_PROJECTS(state, projects) {
      state.featuredProjects = projects;
    },
    SET_CURRENT_PROJECT(state, project) {
      state.currentProject = project;
    },
    SET_RELATED_PROJECTS(state, projects) {
      state.relatedProjects = projects;
    },
    SET_PROJECT_MEDIA(state, media) {
      state.projectMedia = media;
    },
    SET_SEARCH_RESULTS(state, results) {
      state.searchResults = results;
    },
    SET_FILTER(state, { filter, value }) {
      state.filters[filter] = value;
    },
    RESET_FILTERS(state) {
      Object.keys(state.filters).forEach(key => {
        state.filters[key] = null;
      });
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ERROR(state) {
      state.error = null;
    }
  },
  
  actions: {
    /**
     * Fetch all projects
     */
    async fetchProjects({ commit, state }, params = {}) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      // Combine active filters with any additional params
      const filters = { ...params };
      Object.keys(state.filters).forEach(key => {
        if (state.filters[key] !== null) {
          // Convert camelCase to snake_case for API
          const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
          filters[snakeKey] = state.filters[key];
        }
      });
      
      try {
        const response = await projectService.getProjects(filters);
        commit('SET_PROJECTS', response.data.results || response.data);
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch projects');
        console.error('Error fetching projects:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Fetch featured projects
     */
    async fetchFeaturedProjects({ commit }) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await projectService.getFeaturedProjects();
        
        // Process projects to include proper media URLs
        const processedProjects = await Promise.all(response.data.results.map(async project => {
          // If the project has media, fetch the media URL
          if (project.media && project.media.length > 0) {
            // Updated: Use hash_id instead of hash
            const mediaHashId = project.media[0].hash_id || project.media[0].hash;
            
            // If no valid hash, try to use direct URL if available
            if (mediaHashId) {
              try {
                // Use medium size for featured projects
                project.image_url = await mediaService.getMediaUrl(mediaHashId, 'webp_medium');
              } catch (err) {
                console.error(`Error resolving media for project ${project.id}:`, err);
                // Fallback to direct URL if available
                project.image_url = project.media[0].url || project.featured_image_url;
              }
            }
          }
          return project;
        }));
        
        commit('SET_FEATURED_PROJECTS', processedProjects);
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch featured projects');
        console.error('Error fetching featured projects:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Fetch a specific project by ID or slug
     */
    async fetchProject({ commit, dispatch }, id) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await projectService.getProject(id);
        
        // Check if the response indicates a redirect is needed
        if (response.data.redirect) {
          // Set the project data from the response
          commit('SET_CURRENT_PROJECT', response.data.project);
          // Also fetch media and related projects using the actual project ID
          dispatch('fetchProjectMedia', response.data.project.id);
          dispatch('fetchRelatedProjects', response.data.project.id);
          commit('SET_LOADING', false);
          // Return redirect info so the component can handle it
          return { redirect: true, slug: response.data.slug, canonical_url: response.data.canonical_url, project: response.data.project };
        }
        
        commit('SET_CURRENT_PROJECT', response.data);
        // Also fetch media and related projects
        dispatch('fetchProjectMedia', id);
        dispatch('fetchRelatedProjects', id);
        
        return { redirect: false, project: response.data };
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch project details');
        console.error('Error fetching project:', error);
        
        // Check if it's a 404 error (project not found/unpublished)
        const is404 = error.response && error.response.status === 404;
        
        return { 
          redirect: false, 
          error: error.message, 
          status: error.response?.status,
          is404: is404
        };
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Fetch related projects for a specific project
     */
    async fetchRelatedProjects({ commit }, id) {
      try {
        const response = await projectService.getRelatedProjects(id);
        
        // Process related projects to resolve media URLs
        const processedProjects = await Promise.all((response.data.results || response.data).map(async project => {
          if (project.featured_image_url) {
            try {
              // If it's not a full URL, treat it as a hash_id
              if (!project.featured_image_url.startsWith('http') && !project.featured_image_url.startsWith('/')) {
                project.image_url = await mediaService.getMediaUrl(project.featured_image_url, 'webp_medium');
              } else {
                // Already a URL, keep as is
                project.image_url = project.featured_image_url;
              }
            } catch (err) {
              console.error(`Error resolving media for related project ${project.id}:`, err);
            }
          }
          return project;
        }));
        
        commit('SET_RELATED_PROJECTS', processedProjects);
      } catch (error) {
        console.error('Error fetching related projects:', error);
        // Don't set global error for this secondary data
      }
    },
    
    /**
     * Fetch project media items
     */
    async fetchProjectMedia({ commit }, id) {
      try {
        const response = await projectService.getProjectMedia(id);
        const mediaItems = response.data.results || response.data;
        
        // Process each media item to resolve URLs
        const processedMedia = await Promise.all(mediaItems.map(async media => {
          // Check if we need to resolve a hash ID
          if (media.hash_id) {
            try {
              // Resolve the hash_id to multiple image versions
              const mediaUrls = await mediaService.getMediaVersions(media.hash_id);
              return {
                ...media,
                ...mediaUrls
              };
            } catch (err) {
              console.error(`Error resolving media ${media.id || media.hash_id}:`, err);
              return media;
            }
          }
          return media;
        }));
        
        commit('SET_PROJECT_MEDIA', processedMedia);
      } catch (error) {
        console.error('Error fetching project media:', error);
        // Don't set global error for this secondary data
      }
    },
    
    /**
     * Search projects
     */
    async searchProjects({ commit }, query) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await projectService.searchProjects(query);
        commit('SET_SEARCH_RESULTS', response.data.results || response.data);
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to search projects');
        console.error('Error searching projects:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Update a filter value
     */
    updateFilter({ commit, dispatch }, { key, value }) {
      commit('SET_FILTER', { key, value });
      dispatch('fetchProjects');
    },
    
    /**
     * Reset all filters and refetch projects
     */
    resetFilters({ commit, dispatch }) {
      commit('RESET_FILTERS');
      dispatch('fetchProjects');
    },
    
    /**
     * Clear the current project details
     */
    clearCurrentProject({ commit }) {
      commit('SET_CURRENT_PROJECT', null);
      commit('SET_PROJECT_MEDIA', []);
      commit('SET_RELATED_PROJECTS', []);
    },
    
    /**
     * Clear search results
     */
    clearSearchResults({ commit }) {
      commit('SET_SEARCH_RESULTS', []);
    }
  }
}; 